/**
 * 월별 수익률 시각화 카드
 */
import { memo } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MonthlyReturn } from '../../types/performance';
import { formatPercentTick } from '../../utils/formatters';

interface MonthlyReturnsChartProps {
  data: MonthlyReturn[];
}

const formatTooltipLabel = (label: string | number): string => String(label);

const MonthlyReturnsChart = memo(function MonthlyReturnsChart({ data }: MonthlyReturnsChartProps) {
  const decorated = (data || []).map((item) => ({
    ...item,
    color: item.return_pct >= 0 ? '#22c55e' : '#ef4444',
  }));
  const hasData = decorated.length > 0;
  const average =
    decorated.length > 0
      ? decorated.reduce((acc, item) => acc + item.return_pct, 0) /
        decorated.length
      : null;

  return (
    <section className="performance-chart-shell">
      <header className="performance-chart-header">
        <div>
          <h3>월별 수익률</h3>
          <p>양/음수 구간을 컬러로 분리했습니다.</p>
        </div>
        {average !== null && (
          <span className="performance-chart-meta">
            평균 {average.toFixed(2)}%
          </span>
        )}
      </header>
      {hasData ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={decorated} margin={{ top: 16, right: 24, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#223548" />
            <XAxis dataKey="month" stroke="#7890a8" />
            <YAxis tickFormatter={formatPercentTick} width={80} stroke="#7890a8" />
            {average !== null && (
              <ReferenceLine
                y={average}
                stroke="#38bdf8"
                strokeDasharray="4 4"
                label={{
                  value: '평균',
                  fill: '#9fb1c4',
                  position: 'insideTopRight',
                  fontSize: 11,
                }}
              />
            )}
            <Tooltip
              formatter={(value: number) => `${value.toFixed(2)}%`}
              labelFormatter={formatTooltipLabel}
              contentStyle={{
                background: '#071018',
                borderColor: '#223548',
                borderRadius: 8,
                color: '#dbe7f3',
              }}
              labelStyle={{ color: '#dbe7f3' }}
              itemStyle={{ color: '#dbe7f3' }}
            />
            <Bar dataKey="return_pct" radius={[6, 6, 0, 0]}>
              {decorated.map((entry) => (
                <Cell key={`cell-${entry.month}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <EmptyState />
      )}
    </section>
  );
});

export default MonthlyReturnsChart;

function EmptyState() {
  return (
    <div className="performance-chart-empty">
      데이터가 없습니다.
    </div>
  );
}
